import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, MapPin, Phone } from "lucide-react";
import { api } from "../lib/api";
import { IMAGES, STORE } from "../lib/constants";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function HomePage() {
  const [posts, setPosts] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get("/blog-posts").then((r) => setPosts(r.data.slice(0, 2))).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <section className="relative flex min-h-[88vh] items-center overflow-hidden bg-[#111110]">
        <img src="/cabin-interior.jpeg" alt="Fireside glow, dressed for Christmas" className="absolute inset-0 h-full w-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#111110] via-[#111110]/75 to-[#111110]/30" />
        <div className="crow-container relative z-10 grid items-center gap-12 py-24 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-hand text-2xl text-[#E8873A] sm:text-3xl"
          >
            country · primitive · dressed for Christmas
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-4 max-w-3xl font-serif text-4xl font-semibold leading-tight tracking-tight text-[#FDFBF7] sm:text-5xl lg:text-6xl"
          >
            Primitive Treasures &amp; Country Goods for the Heart of Your Home
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-[#E8DFC8]"
          >
            The Olde Crow Boutique is a little country shop filled with candles, art, handmade soaps and unique
            home decor — every piece gathered for homes that love the simple life. Customers often describe it as a warm hug.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-visit-shop-button"
              className="rounded-md bg-[#C27803] px-7 py-3.5 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-[#9A5E02]"
            >
              Plan Your Visit
            </Link>
            <Link
              to="/blog"
              data-testid="hero-journal-button"
              className="rounded-md border border-[#E8DFC8]/60 px-7 py-3.5 text-sm font-semibold text-[#FDFBF7] transition-colors hover:border-[#C27803] hover:text-[#C27803]"
            >
              Read the Chronicles
            </Link>
          </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.92, rotate: 4 }}
            animate={{ opacity: 1, scale: 1, rotate: 2 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mx-auto hidden max-w-md lg:block"
          >
            <img
              src="/logo-stacked.jpeg"
              alt="The Olde Crow Boutique — the sign above our door"
              data-testid="hero-shop-sign"
              className="w-full rounded-md border-4 border-[#D8CDBC] shadow-2xl"
            />
            <p className="mt-4 text-center font-hand text-2xl text-[#E8DFC8]">the sign above our door</p>
          </motion.div>
        </div>
      </section>

      <motion.section {...fadeUp} className="bg-[#C27803] py-16 sm:py-20" data-testid="welcome-sign-section">
        <div className="crow-container grid items-center gap-6 sm:grid-cols-3">
          <img
            src="/welcome-fall-crow.jpeg"
            alt="Primitive folk art — crow on a dinner bell, Welcome Fall"
            loading="lazy"
            data-testid="welcome-fall-crow-image"
            className="w-full rotate-[-2deg] rounded-md border-4 border-[#292725] object-cover shadow-xl"
          />
          <img
            src="/primitive-home-sign.jpeg"
            alt="Country Primitive Home — simple, cozy, timeless"
            loading="lazy"
            data-testid="welcome-sign-image"
            className="w-full rounded-md border-4 border-[#292725] object-cover shadow-xl"
          />
          <img
            src="/sheep-crow-art.jpeg"
            alt="Primitive folk art — sheep and crow with pumpkins"
            loading="lazy"
            data-testid="sheep-crow-image"
            className="w-full rotate-2 rounded-md border-4 border-[#292725] object-cover shadow-xl"
          />
        </div>
      </motion.section>

      {gallery.length > 0 && (
        <motion.section {...fadeUp} className="bg-[#E0D3B6] py-20 sm:py-24" data-testid="shop-gallery-section">
          <div className="crow-container">
            <p className="eyebrow-label">Step Inside the Shop</p>
            <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-[#292725] sm:text-3xl lg:text-4xl">
              A peek at the shelves before you visit
            </h2>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="shop-gallery-grid">
              {gallery.map((p, i) => (
                <figure key={p.id} className={`${i % 2 === 0 ? "rotate-[-1deg]" : "rotate-[1deg]"} overflow-hidden rounded-md border border-[#D8CDBC] bg-[#FDFBF7] shadow-sm`} data-testid={`gallery-photo-${p.id}`}>
                  <img src={p.url} alt={p.caption || "Inside The Olde Crow Boutique"} loading="lazy" className="h-64 w-full object-cover" />
                  {p.caption && (
                    <figcaption className="px-4 py-3 text-center font-hand text-xl text-[#5C554B]">{p.caption}</figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        </motion.section>
      )}

      <motion.section {...fadeUp} className="grain-overlay bg-[#22331F] py-20 sm:py-24" data-testid="christmas-section">
        <div className="crow-container grid items-center gap-12 lg:grid-cols-2">
          <div className="relative order-last lg:order-first">
            <img
              src="/country-kitchen.jpeg"
              alt="A cozy country kitchen dressed for Christmas"
              loading="lazy"
              data-testid="christmas-kitchen-image"
              className="w-full rotate-[-1.5deg] rounded-md border-4 border-[#5A7550] object-cover shadow-2xl"
            />
            <p className="absolute -bottom-5 right-6 rotate-2 rounded-sm bg-[#E8DFC8] px-4 py-2 font-hand text-xl text-[#292725] shadow-md">
              the kettle's on, the candles lit
            </p>
            <div className="mt-10 grid grid-cols-2 gap-4">
              <img src="/dough-bowl-candlelight.jpeg" alt="A dough bowl of winter whites and candlelight" loading="lazy" className="w-full rotate-[1.5deg] rounded-md border-2 border-[#5A7550] object-cover shadow-lg" />
              <img src="/cabin-interior.jpeg" alt="Fireside glow on a winter evening" loading="lazy" className="w-full rotate-[-1.5deg] rounded-md border-2 border-[#5A7550] object-cover shadow-lg" />
            </div>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#D9B45B]">Christmas at the Olde Crow</p>
            <h2 className="mt-4 font-serif text-2xl font-semibold tracking-tight text-[#FDFBF7] sm:text-3xl lg:text-4xl">
              Evergreen, candlelight &amp; country cheer
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-[#E8DFC8]/90">
              Christmas is the shop's favourite season — evergreen on the door, candles in the windows, and shelves
              brimming with gifts that feel found, not bought: primitive folk art, cozy throws, handmade soaps and
              stocking-sized treasures for everyone on your list.
            </p>
            <p className="mt-4 max-w-lg font-hand text-xl text-[#D9B45B]">
              come gather your Christmas — the kettle's on and the tree is lit
            </p>
            <Link
              to="/contact"
              data-testid="christmas-visit-button"
              className="mt-8 inline-block rounded-md bg-[#8C251C] px-7 py-3 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-[#6B1912]"
            >
              Plan Your Christmas Visit
            </Link>
          </div>
        </div>
      </motion.section>

      <motion.section {...fadeUp} className="grain-overlay bg-[#8C251C] py-20 sm:py-24" data-testid="artisan-spotlight-section">
        <div className="crow-container grid items-center gap-12 lg:grid-cols-2">
          <div>
            <p className="font-hand text-2xl text-[#F4EFE6]">gathered &amp; curated with love</p>
            <h2 className="mt-4 font-serif text-2xl font-semibold tracking-tight text-[#FDFBF7] sm:text-3xl lg:text-4xl">
              Chosen slowly, piece by piece
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-[#F4EFE6]/90">
              Nearly everything on our shelves is carefully gathered and curated by the owner — primitive folk art,
              barn stars, cozy throws and candlelight, all in keeping with the country cozy. A few handmade treasures
              find their way in too, but curation is our craft: every piece earns its place.
            </p>
            <Link
              to="/about"
              data-testid="artisan-read-story-button"
              className="mt-8 inline-block rounded-md border border-[#FDFBF7]/70 px-7 py-3 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-[#FDFBF7] hover:text-[#8C251C]"
            >
              Read Our Story
            </Link>
          </div>
          <div className="relative">
            <img src="/fall-porch.jpeg" alt="Fall at the shop — boots, mums and gathered goods" loading="lazy" className="w-full rounded-md border-4 border-[#6B1912] object-cover shadow-2xl" />
            <p className="absolute -bottom-5 left-6 rotate-[-2deg] rounded-sm bg-[#E8DFC8] px-4 py-2 font-hand text-xl text-[#292725] shadow-md">
              gathered for the country cozy
            </p>
          </div>
        </div>
      </motion.section>

      <motion.section {...fadeUp} className="crow-container py-20 sm:py-24" data-testid="journal-teaser-section">
        <p className="eyebrow-label">The Crow Chronicles</p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <h2 className="font-serif text-2xl font-semibold tracking-tight text-[#292725] sm:text-3xl lg:text-4xl">
            Notes from the shop journal
          </h2>
          <Link to="/blog" className="flex items-center gap-1 text-sm font-semibold text-[#8C251C] hover:text-[#6B1912]" data-testid="journal-view-all-link">
            All stories <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 grid gap-8 md:grid-cols-2">
          {posts.map((post) => (
            <Link key={post.id} to={`/blog/${post.id}`} data-testid={`journal-teaser-${post.id}`} className="group grid overflow-hidden rounded-md border border-[#D8CDBC] bg-[#FDFBF7] shadow-sm transition-shadow hover:shadow-md sm:grid-cols-2">
              <div className="h-48 overflow-hidden sm:h-full">
                <img src={post.image_url} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="flex flex-col p-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C27803]">{post.topic}</span>
                <h3 className="mt-2 font-serif text-xl font-semibold leading-snug text-[#292725] group-hover:text-[#8C251C]">{post.title}</h3>
                <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-[#8A7E70]">{post.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </motion.section>

      <section className="grain-overlay bg-[#292725] py-14" data-testid="sister-store-section">
        <div className="crow-container flex flex-col items-center gap-4 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#E8873A]">One Roost Now</p>
          <h2 className="max-w-2xl font-serif text-2xl font-semibold tracking-tight text-[#FDFBF7] sm:text-3xl">
            The Kilted Goat has come home to The Olde Crow
          </h2>
          <p className="max-w-xl text-sm leading-relaxed text-[#B9AE9C]">
            The Kilted Goat Country Store has blended into The Olde Crow Boutique — one shop now, same owner,
            same love of country living. All the goods, all the gathering, all under one roof at The Sunnyside Mall.
          </p>
          <p className="font-hand text-2xl text-[#E8DFC8]">two names, one nest</p>
        </div>
      </section>

      <motion.section {...fadeUp} className="bg-[#F4EFE6] pb-24 pt-4" data-testid="visit-callout-section">
        <div className="crow-container">
          <div className="grid gap-10 rounded-md border border-[#D8CDBC] bg-[#FDFBF7] p-8 shadow-sm sm:p-12 lg:grid-cols-2">
            <div>
              <p className="eyebrow-label">Come Say Hello</p>
              <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight text-[#292725] sm:text-3xl">
                The kettle's always on at {STORE.address.split(",")[0]}
              </h2>
              <p className="mt-2 font-hand text-xl text-[#8C251C]">established 2018 · nesting at Sunnyside Mall for 3 years</p>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-[#8A7E70]">
                Candles, art, handmade soaps and unique home decor in a warm, friendly, cozy space — our staff are
                very welcoming, and customers often describe the shop as a warm hug. We would love to meet you.
              </p>
              <div className="mt-6 space-y-2 text-sm text-[#292725]">
                <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#C27803]" /> {STORE.address}</p>
                <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#C27803]" /> {STORE.phone}</p>
              </div>
              <Link
                to="/contact"
                data-testid="visit-directions-button"
                className="mt-8 inline-block rounded-md bg-[#8C251C] px-7 py-3 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-[#6B1912]"
              >
                Plan Your Visit
              </Link>
            </div>
            <div className="rounded-md border border-[#D8CDBC] bg-[#F4EFE6] p-6">
              <h3 className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-[#8A7E70]">
                <Clock className="h-4 w-4 text-[#C27803]" /> Shop Hours
              </h3>
              <ul className="mt-4 divide-y divide-dashed divide-[#D8CDBC]">
                {STORE.hours.map((h) => (
                  <li key={h.days} className="flex items-center justify-between py-3 text-sm">
                    <span className="font-medium text-[#292725]">{h.days}</span>
                    <span className={h.time === "Closed" ? "font-hand text-lg text-[#8C251C]" : "text-[#8A7E70]"}>{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </motion.section>
    </div>
  );
}
